import { ThermometerSimple } from "@phosphor-icons/react";

import { type HeatingGroup, useAdminHeatingGroups } from "@/lib/api";
import { cn } from "@/lib/utils";

interface HeatingGroupsCardProps {
  householdId: string;
}

/**
 * Which circuits share a thermostat on this household's Wavin.
 *
 * A thermostat can drive several circuits, and the integration then creates
 * one entity for the whole group, named after its lowest circuit. The others
 * are invisible everywhere else in the product, including the resident's
 * mapping screen, so this card is the only place the full picture appears.
 *
 * That grouping is correct when a group sits inside one room and unworkable
 * when it spans several, because the hardware holds one temperature for the
 * whole group. Only the label card on the manifold says which, so a
 * multi-circuit group is flagged for a person to check rather than declared
 * broken.
 */
export function HeatingGroupsCard({ householdId }: HeatingGroupsCardProps) {
  const { data, isLoading } = useAdminHeatingGroups(householdId);

  return (
    <div className="bg-card shadow-elevation rounded-md">
      <div className="border-border-subtle border-b px-5 py-[18px]">
        <h2 className="text-primary text-[17px] font-bold">Heating groups</h2>
        <div className="text-muted mt-0.5 text-[12.5px]">
          Read from the hub. Circuit numbers are the ones printed on the Wavin.
        </div>
      </div>

      {isLoading && <div className="text-muted px-5 py-4 text-[13px]">Reading the hub...</div>}

      {!isLoading && !data?.available && (
        <div className="text-muted px-5 py-4 text-[13px]">
          {data?.detail ?? "The heating groups could not be read."}
        </div>
      )}

      {!isLoading && data?.available && (
        <>
          <div className="text-muted border-border-subtle flex gap-5 border-b px-5 py-3 text-[12.5px]">
            <Count value={data.circuits ?? 0} label="circuits" />
            <Count value={data.thermostats ?? 0} label="thermostats" />
            <Count value={data.rooms_on_record ?? 0} label="rooms on record" />
          </div>
          {data.detail && (
            <div className="text-muted border-border-subtle border-b px-5 py-2.5 text-[12.5px]">
              {data.detail}
            </div>
          )}
          <div className="flex flex-col">
            {(data.groups ?? []).map((group, i) => (
              <Row key={group.primary_circuit} group={group} first={i === 0} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Count({ value, label }: { value: number; label: string }) {
  return (
    <span>
      <span className="text-primary font-semibold">{value}</span> {label}
    </span>
  );
}

/**
 * One thermostat. Amber, not red, when it drives several circuits: a shared
 * thermostat is normal and usually fine, and the only thing we can say from
 * here is that somebody should check whether those circuits are one room.
 */
function Row({ group, first }: { group: HeatingGroup; first: boolean }) {
  const shared = group.circuits.length > 1;
  return (
    <div
      className={cn(
        "flex items-center gap-3 px-5 py-3.5",
        !first && "border-border-subtle border-t",
      )}
    >
      <div
        className={cn(
          "grid size-9 shrink-0 place-items-center rounded-xl",
          shared ? "bg-amber-50 text-amber-700" : "bg-background text-muted",
        )}
      >
        <ThermometerSimple size={18} weight="bold" />
      </div>

      <div className="min-w-0 grow">
        <div className="flex flex-wrap items-center gap-1.5">
          {group.circuits.map((circuit) => (
            <span
              key={circuit}
              className={cn(
                "rounded-md px-1.5 py-0.5 text-[12.5px] font-semibold",
                circuit === group.primary_circuit
                  ? "text-primary bg-background"
                  : "bg-amber-50 text-amber-700",
              )}
            >
              {circuit}
            </span>
          ))}
          <span className="text-muted ml-1 text-[12.5px]">
            {shared ? "one thermostat" : "own thermostat"}
          </span>
        </div>
        {shared && (
          <div className="text-muted mt-1 text-[12px]">
            Only circuit {group.primary_circuit} is offered in the app. Check whether the others are
            the same room.
          </div>
        )}
      </div>

      <div
        className={cn(
          "shrink-0 text-[13px]",
          group.room ? "text-primary font-semibold" : "text-muted",
        )}
      >
        {group.room ?? "not mapped"}
      </div>
    </div>
  );
}
